import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { i18nIntl, migrate_question } from '../../lib/ep.js';

import ActionButton from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import ColorPicker from '../builder/ColorPicker.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout.js';

import CheckEnabler from '../builder/CheckEnabler.js';

import SectionTitle from '../builder/SectionTitle.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import DynamicFields from '../builder/DynamicFields.js';
import HiddenClient from '../front/HiddenClient.js';
import Message from '../builder/Message.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import { v4 as uuidv4 } from 'uuid';
import Checkbox from '../builder/Checkbox.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const Area = ({ model, change })=> {
	return (
		<Model
			model={model}
			change={change}
		>
			<Input
				name="name"
			>Label</Input>
		</Model>
	)
};

const CreateArea = (props)=>{
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		
		<FloatingLayer backButton={props.close}>
			<div>
				<div class="gui_equal_columns gui_padded_children">
					<div>
						<SectionTitle>Create the hidden object areas</SectionTitle>

						<SectionSubtitle>To mark the hidden objects in the image that the participants have to find, click on the image to build different clickable areas (these are invisible to the participant).</SectionSubtitle>

						<ul class="bulleted_list">
							<li>{i18n("Add as many areas as you want.")}</li>

							<li>{i18n("While creating, you can edit the shape of each area and move it around. Once created, it's only possible to edit the name of the area, and you can delete areas and create new ones.")}</li>

							<li>{i18n("Add a label to the area so users can identify what to search for in the image.")}</li>
						</ul>
					</div>

					<div>
						<div>
							<img src={`${window.cdn_url}img/gif/guide_to_create_search_areas.gif`} width="100%" alt="" />
						</div>
					</div>
				</div>

				<ButtonCollection in_floating={true}>
					<ActionButton
						onClick={props.close}
					>Understood. Go now</ActionButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors_game, setErrorsGame ] = useState({});
	const [ create_popup, toggleCreate ] = useState(false);

	const min_attempts = (stage?.hidden_areas ?? []).length;

	const saveAreas = (areas) => {
		change('hidden_areas',areas.map(area=>{
			if (area?.type != undefined) return area;
			return {...area,name:"Area",type:'hidden_area',id:uuidv4()};
		}));
	}

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
				errors={(Object.keys(errors_game).length > 0) ? true : false}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Range
							min={30}
							max={300}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							name="game_time"
							value={Math.max((stage?.game_time ?? 60), 30)}
							_class={{ first:true }}
						>Time limit</Range>

						<CheckEnabler 
							label="Limit moves"
							enabled={(stage?.game_attempts > 0) ? true : false}
							onDisable={() => change('game_attempts', "")}
							onEnable={() => change('game_attempts', min_attempts)}
						>
							<Range
								model={stage}
								change={change}
								min={min_attempts}
								max={50}
								step={1}
								name="game_attempts"
								value={Math.max((stage?.game_attempts ?? 1), min_attempts)}
							>Max. number of attempts/moves</Range>
						</CheckEnabler>

						<Checkbox
							name="display_legend"
							value={stage?.display_legend ?? true}
						>Display legend</Checkbox>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="hidden_image"
							image={stage.full_hidden_image}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_size={{ width:800, height:800 }}
						>Image</AutoPostImageUploader>

						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>

					<DynamicFields
						fields={stage?.hidden_areas ?? []}
						can_create={false}
						can_copy={false}
						sortable={false}
						onChange={(v) => change('hidden_areas', v)}
						field_types={[{
							type:'hidden_area',
							title:(f) => (f.name ?? 'area'),
						}]}
						section_classes='gui_not_in_top_huge'
						section_title={i18n("Areas")}
						section_icon='knk-obj-rect'
					>
						<Area />
					</DynamicFields>

					<Message
						title="Create your own areas"
						_class={{ other:'gui_not_in_top in_bottom' }}
					>
						<p>{i18n("To add new search areas in the game you must click on the image and start building different clickable areas (invisible to the participant) that should be discovered in the participation process.")}</p>

						<ButtonCollection>
							<ActionButton
								onClick={() => toggleCreate(!create_popup)}
							>Create an area</ActionButton>
						</ButtonCollection>
					</Message>
				</article>

				{(create_popup) && (
					<CreateArea 
						promotion={props.promotion}
						intl={props.intl}
						close={() => toggleCreate(false)}
					/>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors_game).length == 0) ? (
							<HiddenClient
								stage={{ Stage:stage }}
								is_builder={true}
								is_playable={true}
								section="settings"
								saveAreas={saveAreas}
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors_game).map((error, i) => {
										if(typeof error === 'object') return null;

										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<ColorPicker
							name="color_ok"
							_class={{ size:60, first:true }}
							color={stage?.color_ok ?? 'rgba(120, 195, 0, 1)'}
						>Correct</ColorPicker>

						<ColorPicker
							name="color_ko"
							_class={{ size:60 }}
							color={stage?.color_ko ?? 'rgba(215, 35, 60, 1)'}
						>Incorrect</ColorPicker>

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<HiddenClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_edit_mode={false}
							section="settings"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<HiddenClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const HiddenBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/hidden`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const migrate_from_question = true;
	if(migrate_from_question) {
		const question = props.questionStore.getState()?.questions.questions.find((q) => (q.Question.stage == props.stage.id));

		const game_stage_fields_map = {
			areas:'hidden_areas',
			time_seconds:'game_time',
			attempts:'game_attempts',
			bg_color:'bg_color'
		}
		if (question) {
			migrate_question(game_stage_fields_map, stage, question.Question);
		}
	};

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<HiddenClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['fail']}
						>
							<HiddenClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(HiddenBuilder);